const paperSize = (width, height) => Object.freeze({ width, height });
export const PAPER_SIZES = Object.freeze({
  A3: paperSize(420, 297),
  'A3+': paperSize(483, 329),
  A2: paperSize(594, 420),
  'A2+': paperSize(624, 456),
}); // landscape, in mm: the paper sizes MoldEditScreen's size select offers, which only its preview's viewBox follows; frozen, as the editor holds the chosen one as state
export const PRINTING_SHEET = PAPER_SIZES.A3; // the sheet every mold is printed on: the printing SVG's viewBox (PrintingRunSvg), the file's size and the PDF's page (ProcessOrderScreen)

const BASE_PUNCHING_HOLES = {
  topLeft: { cx: 19.957, cy: 7.041 },
  topCenter: { cx: 209.939, cy: 7.041 },
  topRight: { cx: 399.929, cy: 7.041 },
  bottomLeft: { cx: 19.957, cy: 290.015 },
  bottomCenter: { cx: 209.939, cy: 290.015 },
  bottomRight: { cx: 399.929, cy: 290.015 },
}; // Base positions (unscaled, no adjustments)

function getPunchingHoleOffsets(scale) {
  const xs = Object.values(BASE_PUNCHING_HOLES).map((h) => h.cx); // collect all original cx values
  const ys = Object.values(BASE_PUNCHING_HOLES).map((h) => h.cy); // collect all original cy values
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2; // compute bounding-box center X
  const centerY = (Math.min(...ys) + Math.max(...ys)) / 2; // compute bounding-box center Y

  const offsets = {}; // will hold dx/dy for each hole

  // iterate over every hole key:
  Object.keys(BASE_PUNCHING_HOLES).forEach((key) => {
    const { cx, cy } = BASE_PUNCHING_HOLES[key]; // grab original cx/cy

    // store calculated offsets:
    offsets[key] = {
      dx: (cx - centerX) * (scale - 1), // offset in X caused by scaling (shift in X toward/away from center)
      dy: (cy - centerY) * (scale - 1), // offset in Y caused by scaling (shift in Y toward/away from center)
    };
  });

  return offsets; // return map: { holeKey: {dx, dy}, ... }
} // Helper to get offsets caused by scaling (calculates how far each punching hole's cx/cy should move when scaling the group around its center). It is important to use the BASE_PUNCHING_HOLES values for the calculation (without the punchingHoleXAdjustments values applied yet).

export function buildPunchingHolePositions(scale, xAdj) {
  const offsets = getPunchingHoleOffsets(scale); // Get how much each hole should shift when scaling around the center
  const pos = {}; // Final positions will be stored here

  // Loop through each punching hole (topLeft, bottomRight, etc.)
  Object.keys(BASE_PUNCHING_HOLES).forEach((key) => {
    pos[key] = {
      cx: BASE_PUNCHING_HOLES[key].cx + offsets[key].dx + (xAdj[key] || 0), // Shift base cx by dx from scaling, plus any manual x adjustment
      cy: BASE_PUNCHING_HOLES[key].cy + offsets[key].dy, // Shift base cy by dy from scaling
    };
  });

  return pos; // Return final cx/cy positions for all holes
} // this function returns the final cx/cy positions of all punching holes after applying scaling and x-axis adjustments

export function recalcKeycapAdjustments(scale, moldArr) {
  const xs = moldArr.map((k) => k.xCenter); // collect all original xCenter values
  const ys = moldArr.map((k) => k.yCenter); // collect all original yCenter values
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2; // compute bounding-box center X
  const centerY = (Math.min(...ys) + Math.max(...ys)) / 2; // compute bounding-box center Y

  return moldArr.map((k) => {
    const dx = (k.xCenter - centerX) * (scale - 1); // offset in X caused by scaling (shift in X toward/away from center)
    const dy = (k.yCenter - centerY) * (scale - 1); // offset in Y caused by scaling (shift in Y toward/away from center)

    return {
      ...k, // keep everything else
      adjustments: {
        ...k.adjustments,
        xCenter: dx, // overwrite with new scale offset
        yCenter: dy, // overwrite with new scale offset
      },
    };
  });
} // Recalculate x/y center offsets for every keycap based on the given scale.

export const getSlotCenter = (slot) => {
  const ax = slot.adjustments?.xCenter ?? 0; // offset from scaling
  const ay = slot.adjustments?.yCenter ?? 0; // offset from scaling

  const mx = slot.adjustments?.manualXAdj ?? 0; // manual adjustment of the x coordinate
  const my = slot.adjustments?.manualYAdj ?? 0; // manual adjustment of the y coordinate

  return {
    cx: slot.xCenter + ax + mx, // adjusted center X
    cy: slot.yCenter + ay + my, // adjusted center Y
  };
}; // the centre a slot's post is printed at: the stored centre, moved by the keycap scaling's offset and the manual adjustment. The slot is one of the mold's, or a keycap that carries its slot's fields (moldAssignment.js)

export const getSlotShadowPosition = (slot) => {
  const { cx, cy } = getSlotCenter(slot);

  if (slot.isIsoEnter) {
    return { x: cx - (1.5 * 19.05) / 2, y: cy - (2 * 19.05) / 2 }; // the ISO Enter's shadow is 1.5u by 2u
  }

  if (slot.width === 1 && slot.height === 2) {
    return {
      x: cx - (slot.height * 19.05) / 2,
      y: cy - (slot.width * 19.05) / 2,
    }; // a 1x2 keycap lies on its side on the mold: right for XDA, where it shares the 2x1 slot, wrong for a standing 1x2 of another profile (KNOWN_BUGS.md, 1x2 shadows on other profiles)
  }

  return {
    x: cx - (slot.width * 19.05) / 2,
    y: cy - (slot.height * 19.05) / 2,
  };
}; // the top-left corner of the keycap shadow drawn at a slot (processOrderScreen/StaticKeycapShadows), from the slot's centre

export const getSlotPrintPlacement = (slot) => {
  if (slot.width === 1 && slot.height === 2) {
    const { cx, cy } = getSlotCenter(slot);
    return {
      x: cx - (slot.width * 19.05) / 2,
      y: cy - (slot.height * 19.05) / 2,
      rotation: -90,
    }; // a 1x2 keycap lies on its side on the mold: its legends are drawn standing and turned a quarter turn about the slot's centre, onto the lying footprint getSlotShadowPosition gives (right for XDA; Known bug (KNOWN_BUGS.md, 1x2 shadows on other profiles))
  }

  return { ...getSlotShadowPosition(slot), rotation: 0 }; // every other keycap, the ISO Enter included, prints unturned on its shadow's footprint (the function spelled the two corners out again until a chain 10 review)
}; // where the order processing's overlay draws a keycap's legends at its slot (processOrderScreen/KeycapOverlay, which set these per type until chain 10, Oct 2026): the top-left corner of the keycap's body around the slot's centre, and the turn applied about that centre

export const getSlotPrintArea = (slot) => {
  const { cx, cy } = getSlotCenter(slot);

  return {
    x: cx - slot.w / 2,
    y: cy - slot.h / 2,
    width: slot.w,
    height: slot.h,
  };
}; // the rectangle a slot's keycap colour is printed on: the slot's printed area (w by h) around its adjusted centre, as ProcessOrderScreen's printing files (PrintingRunSvg) and MoldEditScreen's preview draw it (each had its own copy until chain 12)
